import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowLeft,
  ArrowRight,
  Link2,
  LoaderCircle,
} from "lucide-react";
import { toast } from "sonner";

import { api, getApiErrorMessage } from "@/lib/api";
import { useTerritoryCatalog } from "@/hooks/useTerritoryCatalog";

import {
  DIRECTORY_PARTICIPATION_OPTIONS,
  hasText,
  INITIATIVE_INITIAL,
  isValidEmail,
  isValidPassword,
  isValidPhone,
  MAP_PARTICIPATION_OPTIONS,
  PERSONAL_INITIAL,
  PUBLICATION_INITIAL,
  PUBLICATION_TYPE_BY_OFFER,
} from "./register-wizard/config";
import {
  ConfirmationStep,
  InitiativeStep,
  PersonalStep,
  PublicationStep,
} from "./register-wizard/steps";
import { SectionHeader, StepRail } from "./register-wizard/primitives";
import {
  calculateUploadProgress,
  validateRegistrationImage,
  validateRegistrationImages,
} from "./register-wizard/registration-media";

const REGISTER_DRAFT_KEY = "polenica:registration-draft:v2";
const DRAFT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

async function uploadSingleFile(file, onProgress) {
  const formData = new FormData();
  formData.append("file", file);

  const response = await api.post("/uploads/public", formData, {
    timeout: 120000,
    onUploadProgress: (event) => onProgress?.(calculateUploadProgress(event)),
  });

  return response.data;
}

function normalizeTerritorySelection(municipio, comuna, barrio) {
  if (municipio !== "Medellin") {
    return {
      comuna: comuna || "No aplica",
      barrio: barrio || "No aplica",
    };
  }

  return {
    comuna,
    barrio,
  };
}

export default function RegisterWizard({ register, onSuccess }) {
  const { loading: territoryLoading, municipalities, getComunasForMunicipio, getBarriosForComuna } = useTerritoryCatalog();

  const [step, setStep] = useState(1);
  const [attemptedSteps, setAttemptedSteps] = useState({});
  const [personal, setPersonal] = useState(PERSONAL_INITIAL);
  const [wizard, setWizard] = useState({
    mapParticipation: "",
    iniciativa_existente_id: null,
    directoryChoice: "",
  });
  const [initiative, setInitiative] = useState(INITIATIVE_INITIAL);
  const [initiativeFile, setInitiativeFile] = useState(null);
  const [initiativeUpload, setInitiativeUpload] = useState(null);
  const [initiativePreviewUrl, setInitiativePreviewUrl] = useState("");
  const [publication, setPublication] = useState(PUBLICATION_INITIAL);
  const [publicationFiles, setPublicationFiles] = useState([]);
  const [publicationUploads, setPublicationUploads] = useState([]);
  const [publicationLocalPreviews, setPublicationLocalPreviews] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [publishedInitiatives, setPublishedInitiatives] = useState([]);
  const [selectedExistingInitiative, setSelectedExistingInitiative] = useState(null);
  const [initiativeUploading, setInitiativeUploading] = useState(false);
  const [publicationUploading, setPublicationUploading] = useState(false);
  const [initiativeUploadProgress, setInitiativeUploadProgress] = useState(0);
  const [publicationUploadProgress, setPublicationUploadProgress] = useState(0);
  const [finalAcknowledged, setFinalAcknowledged] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [draftReady, setDraftReady] = useState(false);
  const [draftRestored, setDraftRestored] = useState(false);
  const initiativeUploadPromiseRef = useRef(null);
  const publicationUploadPromiseRef = useRef(null);
  const submitInFlightRef = useRef(false);

  useEffect(() => {
    try {
      const stored = window.localStorage.getItem(REGISTER_DRAFT_KEY);
      if (!stored) {
        return;
      }

      const draft = JSON.parse(stored);
      if (!draft?.savedAt || Date.now() - draft.savedAt > DRAFT_MAX_AGE_MS) {
        window.localStorage.removeItem(REGISTER_DRAFT_KEY);
        return;
      }

      if (draft.personal) {
        setPersonal({ ...PERSONAL_INITIAL, ...draft.personal, password: "" });
      }
      if (draft.wizard) {
        setWizard((current) => ({ ...current, ...draft.wizard }));
      }
      if (draft.initiative) {
        setInitiative({ ...INITIATIVE_INITIAL, ...draft.initiative });
      }
      if (draft.publication) {
        setPublication({ ...PUBLICATION_INITIAL, ...draft.publication });
      }
      if (draft.initiativeUpload) {
        setInitiativeUpload(draft.initiativeUpload);
      }
      if (Array.isArray(draft.publicationUploads)) {
        setPublicationUploads(draft.publicationUploads);
      }
      setStep(Math.min(4, Math.max(1, Number(draft.step) || 1)));
      setFinalAcknowledged(Boolean(draft.finalAcknowledged));
      setDraftRestored(true);
    } catch (_error) {
      window.localStorage.removeItem(REGISTER_DRAFT_KEY);
    } finally {
      setDraftReady(true);
    }
  }, []);

  useEffect(() => {
    if (!draftReady || successMessage) {
      return;
    }

    const { password: _password, ...safePersonal } = personal;
    window.localStorage.setItem(
      REGISTER_DRAFT_KEY,
      JSON.stringify({
        savedAt: Date.now(),
        step,
        personal: safePersonal,
        wizard,
        initiative,
        initiativeUpload,
        publication,
        publicationUploads,
        finalAcknowledged,
      }),
    );
  }, [
    draftReady,
    finalAcknowledged,
    initiative,
    initiativeUpload,
    personal,
    publication,
    publicationUploads,
    step,
    successMessage,
    wizard,
  ]);

  useEffect(() => {
    let active = true;

    api
      .get("/initiatives")
      .then((response) => {
        if (!active) {
          return;
        }

        const items = (response.data?.features || []).map((feature) => ({
          id: feature?.properties?.id,
          nombre: feature?.properties?.nombre,
          tipo: feature?.properties?.tipo,
          municipio: feature?.properties?.municipio,
          comuna: feature?.properties?.comuna,
          barrio: feature?.properties?.barrio,
          direccion: feature?.properties?.direccion,
        }));

        setPublishedInitiatives(items.filter((item) => item.id && item.nombre));
      })
      .catch(() => {
        setPublishedInitiatives([]);
      });

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!initiativeFile) {
      setInitiativePreviewUrl("");
      return undefined;
    }

    const nextUrl = URL.createObjectURL(initiativeFile);
    setInitiativePreviewUrl(nextUrl);

    return () => {
      URL.revokeObjectURL(nextUrl);
    };
  }, [initiativeFile]);

  useEffect(() => {
    if (!publicationFiles.length) {
      setPublicationLocalPreviews([]);
      return undefined;
    }

    const nextUrls = publicationFiles.map((file) => URL.createObjectURL(file));
    setPublicationLocalPreviews(nextUrls);

    return () => {
      nextUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [publicationFiles]);

  const publicationPreviewUrls = publicationUploads.length
    ? publicationUploads.map((item) => item.secure_url)
    : publicationLocalPreviews;

  const personalErrors = useMemo(() => {
    const errors = {};
    if (!hasText(personal.nombres, 2)) {
      errors.nombres = "Ingrese al menos 2 caracteres.";
    }
    if (!hasText(personal.apellidos, 2)) {
      errors.apellidos = "Ingrese al menos 2 caracteres.";
    }
    if (!isValidEmail(personal.email)) {
      errors.email = "Ingrese un correo válido.";
    }
    if (!isValidPassword(personal.password)) {
      errors.password = "Debe tener mínimo 8 caracteres, una mayúscula y un número.";
    }
    return errors;
  }, [personal]);

  const initiativeErrors = useMemo(() => {
    const errors = {};
    const territory = normalizeTerritorySelection(initiative.municipio, initiative.comuna, initiative.barrio);

    if (!wizard.mapParticipation) {
      errors.mapParticipation = "Debe indicar cómo desea continuar con el paso del mapa.";
      return errors;
    }

    if (wizard.mapParticipation === "existing" && !wizard.iniciativa_existente_id) {
      errors.iniciativa_existente_id = "Seleccione la iniciativa existente.";
      return errors;
    }

    if (wizard.mapParticipation !== "new") {
      return errors;
    }

    if (!hasText(initiative.nombre, 3)) {
      errors.nombre = "Use al menos 3 caracteres.";
    }
    if (!hasText(initiative.descripcion, 10)) {
      errors.descripcion = "Describa la iniciativa con al menos 10 caracteres.";
    }
    if (!hasText(initiative.direccion, 5)) {
      errors.direccion = "La dirección es obligatoria.";
    }
    if (!hasText(initiative.municipio, 2)) {
      errors.municipio = "Municipio obligatorio.";
    }
    if (!hasText(territory.comuna, 2)) {
      errors.comuna = "Comuna obligatoria.";
    }
    if (!hasText(territory.barrio, 2)) {
      errors.barrio = "Barrio obligatorio.";
    }
    if (!hasText(initiative.nombre_contacto, 3)) {
      errors.nombre_contacto = "Ingrese un nombre de contacto válido.";
    }
    if (!isValidPhone(initiative.celular_contacto)) {
      errors.celular_contacto = "Ingrese un celular válido.";
    }
    if (!isValidEmail(initiative.email_contacto)) {
      errors.email_contacto = "Ingrese un correo válido.";
    }
    if (initiative.ano_inicio && (Number(initiative.ano_inicio) < 1900 || Number(initiative.ano_inicio) > 2100)) {
      errors.ano_inicio = "Ingrese un año entre 1900 y 2100.";
    }
    if (!initiative.enfoque_poblacional.length) {
      errors.enfoque_poblacional = "Seleccione al menos un enfoque poblacional.";
    }
    if (!initiativeFile && !initiativeUpload) {
      errors.image = "Debe cargar una imagen principal.";
    }

    return errors;
  }, [initiative, initiativeFile, initiativeUpload, wizard]);

  const publicationErrors = useMemo(() => {
    const errors = {};
    const territory = normalizeTerritorySelection(publication.municipio, publication.comuna, publication.barrio);

    if (!wizard.directoryChoice) {
      errors.directoryChoice = "Debe responder si desea compartir una oferta.";
      return errors;
    }

    if (wizard.directoryChoice !== "share") {
      return errors;
    }

    if (!hasText(publication.titulo, 3)) {
      errors.titulo = "Ingrese un título con al menos 3 caracteres.";
    }
    if (!hasText(publication.descripcion, 10)) {
      errors.descripcion = "Describa la oferta con al menos 10 caracteres.";
    }
    if (!hasText(publication.municipio, 2)) {
      errors.municipio = "Municipio obligatorio.";
    }
    if (!hasText(territory.comuna, 2)) {
      errors.comuna = "Comuna obligatoria.";
    }
    if (!hasText(publication.nombre_contacto, 3)) {
      errors.nombre_contacto = "Ingrese un nombre de contacto válido.";
    }
    if (!isValidPhone(publication.celular_contacto)) {
      errors.celular_contacto = "Ingrese un celular válido.";
    }
    if (!isValidEmail(publication.email_contacto)) {
      errors.email_contacto = "Ingrese un correo válido.";
    }
    if (!publicationFiles.length && !publicationUploads.length) {
      errors.imagenes = "Agregue al menos una imagen.";
    }
    if (!publication.acepta_deslinde_responsabilidad) {
      errors.acepta_deslinde_responsabilidad = "Debe aceptar el deslinde de responsabilidad.";
    }

    return errors;
  }, [publication, publicationFiles.length, publicationUploads.length, wizard.directoryChoice]);

  const confirmationErrors = useMemo(() => {
    const errors = {};
    if (!personal.acepta_politica_datos) {
      errors.acepta_politica_datos = "Debe aceptar la política de tratamiento de datos.";
    }
    if (!finalAcknowledged) {
      errors.finalAcknowledged = "Debe confirmar que la información es correcta.";
    }
    return errors;
  }, [finalAcknowledged, personal.acepta_politica_datos]);

  const completed = {
    1: Object.keys(personalErrors).length === 0,
    2: wizard.mapParticipation ? Object.keys(initiativeErrors).length === 0 : false,
    3: wizard.directoryChoice ? Object.keys(publicationErrors).length === 0 : false,
    4: false,
  };

  const hydrateContactDefaults = () => {
    const fullName = `${personal.nombres} ${personal.apellidos}`.trim();
    const personalTerritory = normalizeTerritorySelection(personal.municipio, personal.comuna, personal.barrio);

    setPersonal((current) => ({
      ...current,
      comuna: personalTerritory.comuna,
      barrio: personalTerritory.barrio,
    }));

    setInitiative((current) => ({
      ...current,
      nombre_contacto: current.nombre_contacto || fullName,
      celular_contacto: current.celular_contacto || personal.celular,
      email_contacto: current.email_contacto || personal.email,
      ciudad: current.municipio || personal.municipio,
      municipio: current.municipio || personal.municipio,
      comuna: current.comuna || personalTerritory.comuna,
      barrio: current.barrio || personalTerritory.barrio,
    }));

    setPublication((current) => ({
      ...current,
      nombre_contacto: current.nombre_contacto || fullName,
      celular_contacto: current.celular_contacto || personal.celular,
      email_contacto: current.email_contacto || personal.email,
      municipio: current.municipio || personal.municipio,
      comuna: current.comuna || personalTerritory.comuna,
      barrio: current.barrio || personalTerritory.barrio,
    }));
  };

  const markStepAttempted = (stepId) => {
    setAttemptedSteps((current) => ({
      ...current,
      [stepId]: true,
    }));
  };

  const ensureInitiativeUploaded = async () => {
    if (wizard.mapParticipation !== "new") {
      return initiativeUpload;
    }
    if (initiativeUpload) {
      return initiativeUpload;
    }
    if (!initiativeFile) {
      return null;
    }
    if (initiativeUploadPromiseRef.current) {
      return initiativeUploadPromiseRef.current;
    }

    setInitiativeUploading(true);
    setInitiativeUploadProgress(0);
    const uploadPromise = uploadSingleFile(initiativeFile, setInitiativeUploadProgress);
    initiativeUploadPromiseRef.current = uploadPromise;
    try {
      const uploaded = await uploadPromise;
      setInitiativeUpload(uploaded);
      return uploaded;
    } finally {
      initiativeUploadPromiseRef.current = null;
      setInitiativeUploading(false);
    }
  };

  const ensurePublicationUploaded = async () => {
    if (wizard.directoryChoice !== "share") {
      return publicationUploads;
    }
    if (publicationUploads.length) {
      return publicationUploads;
    }
    if (!publicationFiles.length) {
      return [];
    }
    if (publicationUploadPromiseRef.current) {
      return publicationUploadPromiseRef.current;
    }

    setPublicationUploading(true);
    setPublicationUploadProgress(0);
    const uploadPromise = (async () => {
      const uploads = [];
      const selectedFiles = publicationFiles.slice(0, 5);
      for (let index = 0; index < selectedFiles.length; index += 1) {
        const file = selectedFiles[index];
        // eslint-disable-next-line no-await-in-loop
        const uploaded = await uploadSingleFile(file, (fileProgress) => {
          const overallProgress = Math.round(
            ((index + fileProgress / 100) / selectedFiles.length) * 100,
          );
          setPublicationUploadProgress(overallProgress);
        });
        uploads.push(uploaded);
      }
      return uploads;
    })();
    publicationUploadPromiseRef.current = uploadPromise;
    try {
      const uploads = await uploadPromise;
      setPublicationUploads(uploads);
      return uploads;
    } finally {
      publicationUploadPromiseRef.current = null;
      setPublicationUploading(false);
    }
  };

  const handleInitiativeFileChange = (event) => {
    const selectedFile = event.target.files?.[0] || null;
    const validationError = selectedFile ? validateRegistrationImage(selectedFile) : null;
    if (validationError) {
      setError(validationError);
      setInitiativeFile(null);
      setInitiativeUpload(null);
      event.target.value = "";
      return;
    }

    setError("");
    setInitiativeUploadProgress(0);
    setInitiativeFile(selectedFile);
    setInitiativeUpload(null);
  };

  const handlePublicationFilesChange = (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    const validationError = validateRegistrationImages(selectedFiles);
    if (validationError) {
      setError(validationError);
      setPublicationFiles([]);
      setPublicationUploads([]);
      event.target.value = "";
      return;
    }

    setError("");
    setPublicationUploadProgress(0);
    setPublicationFiles(selectedFiles);
    setPublicationUploads([]);
  };

  const nextStep = async () => {
    setError("");
    markStepAttempted(step);

    if (step === 1) {
      if (Object.keys(personalErrors).length) {
        setError("Revise los datos personales antes de continuar.");
        return;
      }
      hydrateContactDefaults();
      setStep(2);
      return;
    }

    if (step === 2) {
      if (Object.keys(initiativeErrors).length) {
        setError("Revise la decisión sobre el mapa y complete los campos requeridos.");
        return;
      }

      try {
        await ensureInitiativeUploaded();
        setStep(3);
      } catch (uploadError) {
        setError(getApiErrorMessage(uploadError, "No fue posible cargar la imagen de la iniciativa."));
      }
      return;
    }

    if (step === 3) {
      if (Object.keys(publicationErrors).length) {
        setError("Revise la información del directorio antes de avanzar.");
        return;
      }

      try {
        await ensurePublicationUploaded();
        setStep(4);
      } catch (uploadError) {
        setError(getApiErrorMessage(uploadError, "No fue posible cargar las imágenes de la publicación."));
      }
    }
  };

  const previousStep = () => {
    setError("");
    setStep((current) => Math.max(1, current - 1));
  };

  const handleStepSelect = (targetStep) => {
    setError("");
    markStepAttempted(step);
    if (targetStep > 1 && Object.keys(personalErrors).length === 0) {
      hydrateContactDefaults();
    }
    setStep(targetStep);
  };

  const findFirstInvalidStep = () => {
    if (Object.keys(personalErrors).length) {
      return 1;
    }
    if (Object.keys(initiativeErrors).length) {
      return 2;
    }
    if (Object.keys(publicationErrors).length) {
      return 3;
    }
    if (Object.keys(confirmationErrors).length) {
      return 4;
    }
    return null;
  };

  const handleSubmit = async () => {
    if (submitInFlightRef.current) {
      return;
    }

    setError("");
    markStepAttempted(4);

    const invalidStep = findFirstInvalidStep();
    if (invalidStep) {
      setStep(invalidStep);
      setError("Aún hay campos pendientes o confirmaciones sin revisar.");
      return;
    }

    submitInFlightRef.current = true;
    setSubmitting(true);

    try {
      const uploadedInitiative = await ensureInitiativeUploaded();
      const uploadedPublication = await ensurePublicationUploaded();
      const normalizedPersonalTerritory = normalizeTerritorySelection(
        personal.municipio,
        personal.comuna,
        personal.barrio,
      );
      const normalizedInitiativeTerritory = normalizeTerritorySelection(
        initiative.municipio,
        initiative.comuna,
        initiative.barrio,
      );
      const normalizedPublicationTerritory = normalizeTerritorySelection(
        publication.municipio,
        publication.comuna,
        publication.barrio,
      );

      const payload = {
        ...personal,
        celular: personal.celular || "No aplica",
        municipio: personal.municipio || "No aplica",
        comuna: normalizedPersonalTerritory.comuna,
        barrio: normalizedPersonalTerritory.barrio,
        esta_vinculado_iniciativa: wizard.mapParticipation === "existing",
        iniciativa_en_sistema: wizard.mapParticipation === "existing" ? true : null,
        iniciativa_existente_id: wizard.mapParticipation === "existing" ? wizard.iniciativa_existente_id : null,
        quiere_registrar_iniciativa: wizard.mapParticipation === "new",
        tiene_productos_servicios: wizard.directoryChoice === "share",
        iniciativa:
          wizard.mapParticipation === "new"
            ? {
                ...initiative,
                comuna: normalizedInitiativeTerritory.comuna,
                barrio: normalizedInitiativeTerritory.barrio,
                ciudad: initiative.municipio,
                ano_inicio: initiative.ano_inicio ? Number(initiative.ano_inicio) : null,
                image_url: uploadedInitiative?.secure_url || null,
                asset_public_id: uploadedInitiative?.asset_public_id || null,
              }
            : null,
        publicacion:
          wizard.directoryChoice === "share"
            ? {
                ...publication,
                direccion: publication.direccion || "No aplica",
                comuna: normalizedPublicationTerritory.comuna,
                barrio: normalizedPublicationTerritory.barrio || "No aplica",
                tipo_publicacion: PUBLICATION_TYPE_BY_OFFER[publication.tipo_oferta],
                precio: null,
                detalle_valor: null,
                vigencia: "indefinido",
                imagenes: uploadedPublication.map((item) => item.secure_url),
                asset_public_ids: uploadedPublication.map((item) => item.asset_public_id),
                confirmo_vista_previa: true,
              }
            : null,
      };

      const response = await register(payload);
      const nextMessage =
        response.message || "Registro completado. Su cuenta y el contenido creado ya están activos.";
      setSuccessMessage(nextMessage);
      window.localStorage.removeItem(REGISTER_DRAFT_KEY);
      toast.success(nextMessage);
    } catch (submitError) {
      setError(getApiErrorMessage(submitError, "No fue posible completar el registro."));
    } finally {
      submitInFlightRef.current = false;
      setSubmitting(false);
    }
  };

  const stepHasAttempt = attemptedSteps[step];

  return (
    <div className="rounded-[30px] border border-primary/10 bg-white p-6 shadow-[0_24px_60px_rgba(59,90,63,0.08)]">
      <SectionHeader
        eyebrow="Registro guiado"
        title="Cree su cuenta y haga parte de Polénica"
        description="Regístrese para sumar sus aportes a esta red que conecta saberes, huertas, pacas urbanas, productos, servicios y encuentros que polinizan Medellín."
      />

      {!successMessage ? <StepRail step={step} completed={completed} onStepSelect={handleStepSelect} /> : null}

      {draftRestored && !successMessage ? (
        <div className="mb-5 rounded-[20px] border border-primary/15 bg-primary/5 px-4 py-3 text-sm text-primary">
          Recuperamos la información que había diligenciado. Por seguridad, vuelva a ingresar la contraseña.
        </div>
      ) : null}

      {successMessage ? (
        <div className="mb-5 rounded-[24px] border border-[#7D9D54]/40 bg-[#EFF7E7] px-5 py-4 text-sm font-medium leading-6 text-primary">
          <p>{successMessage}</p>
          <p className="mt-2 font-normal">
            Su cuenta quedó activa. Las iniciativas o publicaciones creadas ya están disponibles en la plataforma.
          </p>
          <button
            type="button"
            onClick={() => window.location.assign("/perfil")}
            className="mt-4 inline-flex rounded-2xl bg-primary px-5 py-2.5 text-sm font-semibold text-primaryForeground"
          >
            Continuar a mi perfil
          </button>
        </div>
      ) : null}

      {error ? (
        <div className="mb-5 rounded-2xl border border-[#D68B72]/40 bg-[#FFF5F1] px-4 py-3 text-sm text-[#8A3D22]">
          {error}
        </div>
      ) : null}

      {!successMessage && step === 1 ? (
        <PersonalStep
          personal={personal}
          personalErrors={personalErrors}
          stepHasAttempt={stepHasAttempt}
          setPersonal={setPersonal}
        />
      ) : null}

      {!successMessage && step === 2 ? (
        <InitiativeStep
          wizard={wizard}
          initiative={initiative}
          initiativeErrors={initiativeErrors}
          stepHasAttempt={stepHasAttempt}
          publishedInitiatives={publishedInitiatives}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          selectedExistingInitiative={selectedExistingInitiative}
          setSelectedExistingInitiative={setSelectedExistingInitiative}
          initiativeFile={initiativeFile}
          initiativeUpload={initiativeUpload}
          initiativePreviewUrl={initiativePreviewUrl}
          setWizard={setWizard}
          setInitiative={setInitiative}
          onInitiativeFileChange={handleInitiativeFileChange}
          mapOptions={MAP_PARTICIPATION_OPTIONS}
          municipalities={municipalities}
          getComunasForMunicipio={getComunasForMunicipio}
          getBarriosForComuna={getBarriosForComuna}
        />
      ) : null}

      {!successMessage && step === 3 ? (
        <PublicationStep
          wizard={wizard}
          publication={publication}
          publicationErrors={publicationErrors}
          stepHasAttempt={stepHasAttempt}
          publishedInitiatives={publishedInitiatives}
          publicationFiles={publicationFiles}
          publicationUploads={publicationUploads}
          publicationPreviewUrls={publicationPreviewUrls}
          setWizard={setWizard}
          setPublication={setPublication}
          onPublicationFilesChange={handlePublicationFilesChange}
          municipalities={municipalities}
          getComunasForMunicipio={getComunasForMunicipio}
          getBarriosForComuna={getBarriosForComuna}
          directoryOptions={DIRECTORY_PARTICIPATION_OPTIONS}
        />
      ) : null}

      {!successMessage && step === 4 ? (
        <ConfirmationStep
          personal={personal}
          wizard={wizard}
          selectedExistingInitiative={selectedExistingInitiative}
          initiative={initiative}
          publication={publication}
          publicationPreviewUrls={publicationPreviewUrls}
          finalAcknowledged={finalAcknowledged}
          confirmationErrors={confirmationErrors}
          stepHasAttempt={stepHasAttempt}
          setFinalAcknowledged={setFinalAcknowledged}
          setPersonal={setPersonal}
          onEditStep={handleStepSelect}
        />
      ) : null}

      {!successMessage ? <div className="mt-6 flex flex-col gap-3 border-t border-border pt-5 sm:flex-row sm:items-center sm:justify-between">
        <button
          type="button"
          onClick={previousStep}
          disabled={step === 1 || submitting || initiativeUploading || publicationUploading}
          className="inline-flex items-center justify-center gap-2 rounded-2xl border border-border px-5 py-3 text-sm font-semibold text-textMain transition hover:border-primary/30 disabled:cursor-not-allowed disabled:opacity-45"
        >
          <ArrowLeft className="h-4 w-4" />
          Volver
        </button>

        {step < 4 ? (
          <button
            type="button"
            onClick={nextStep}
            disabled={initiativeUploading || publicationUploading}
            className="inline-flex items-center justify-center gap-2 rounded-2xl bg-primary px-5 py-3 text-sm font-semibold text-primaryForeground shadow-[0_16px_28px_rgba(59,90,63,0.18)] disabled:cursor-not-allowed disabled:opacity-65"
          >
            {initiativeUploading || publicationUploading ? (
              <LoaderCircle className="h-4 w-4 animate-spin" />
            ) : (
              <ArrowRight className="h-4 w-4" />
            )}
            {initiativeUploading
              ? initiativeUploadProgress >= 100
                ? "Procesando imagen..."
                : `Cargando imagen ${initiativeUploadProgress}%`
              : publicationUploading
                ? publicationUploadProgress >= 100
                  ? "Procesando imágenes..."
                  : `Cargando imágenes ${publicationUploadProgress}%`
                : "Continuar"}
          </button>
        ) : (
          <button
            type="button"
            disabled={submitting}
            onClick={handleSubmit}
            className="inline-flex items-center justify-center gap-2 rounded-2xl bg-primary px-5 py-3 text-sm font-semibold text-primaryForeground shadow-[0_16px_28px_rgba(59,90,63,0.18)] disabled:cursor-not-allowed disabled:opacity-65"
          >
            {submitting ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />}
            Confirmar registro
          </button>
        )}
      </div> : null}
    </div>
  );
}
